import { TaskDto } from "@growth/dto";
import { LoaderCircle } from "lucide-react";
import { Link } from "react-router-dom";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useTasks } from "../lib/content";
import { formatRelative } from "../lib/format";

const GROUPS: { title: string; about: string; types: string[] }[] = [
  {
    title: "To approve",
    about: "Agents finished these. Read, edit if needed, approve or reject.",
    types: ["approve"],
  },
  {
    title: "Needs you",
    about: "Missed the quality bar or the claims check: decide what happens.",
    types: ["decide", "unblock"],
  },
  {
    title: "To post",
    about: "Approved. Post by hand, then paste the live link.",
    types: ["post_manually"],
  },
];

/** Everything waiting on a person for the active brand: the human-oversight queue. */
export const InboxPage = () => {
  const { brand } = useActive();
  const tasks = useTasks();

  return (
    <section className="max-w-4xl space-y-8">
      <PageHeader eyebrow={brand?.name ?? ""} title="Inbox">
        What the agents need from a person.
      </PageHeader>
      {tasks.isError && <ErrorText>{apiErrorMessage(tasks.error)}</ErrorText>}
      {tasks.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {tasks.data?.length === 0 && (
        <p className="rounded-xl border border-line bg-white px-4 py-8 text-center text-sm text-muted">
          Nothing needs you right now.
        </p>
      )}
      {tasks.data &&
        GROUPS.map((group) => {
          const items = tasks.data.filter((task) => group.types.includes(task.type));
          if (!items.length) return null;
          return (
            <div key={group.title} className="space-y-2">
              <h2 className="text-sm font-semibold">
                {group.title} ({items.length})
              </h2>
              <p className="text-xs text-muted">{group.about}</p>
              <ul className="divide-y divide-line rounded-xl border border-line bg-white">
                {items.map((task) => (
                  <TaskRow key={task.id} task={task} />
                ))}
              </ul>
            </div>
          );
        })}
    </section>
  );
};

const TaskRow = ({ task }: { task: TaskDto }) => (
  <li>
    <Link
      to={`/content/${task.pieceId}`}
      className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-paper/50"
    >
      <span className="min-w-0">
        <span className="block truncate text-ink">{task.title}</span>
        <span className="text-xs text-muted">{formatRelative(task.createdAt)}</span>
      </span>
      {task.busy && (
        <LoaderCircle className="size-4 shrink-0 animate-spin text-muted" aria-label="Working" />
      )}
    </Link>
  </li>
);
